import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { motion } from 'framer-motion';
import { Plus, Edit2, Trash2, Settings as SettingsIcon } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '../components/ui/dialog';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { toast } from 'sonner';
import PageTitle from '../components/PageTitle';
import { FADE_IN_UP_HIDDEN, FADE_IN_UP_SHOWN } from '../lib/animations';
import { ICON_OPTIONS, getAccountIcon } from '../lib/accountIcons';
import ConfirmDialog from '../components/ConfirmDialog';
import useUndoDelete from '../hooks/useUndoDelete';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const emptyForm = () => ({ name: '', kind: 'asset', icon: 'CircleDollarSign', default_counts_toward_shortfall: null });

const AccountTypes = () => {
  const [types, setTypes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(emptyForm());
  const [pendingDelete, setPendingDelete] = useState(null);
  const { scheduleDelete } = useUndoDelete();

  const fetchTypes = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/account-types`, { withCredentials: true });
      setTypes(res.data);
    } catch (e) {
      toast.error('Failed to load account types');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchTypes();
  }, [fetchTypes]);

  const openNew = () => {
    setEditing(null);
    setForm(emptyForm());
    setDialogOpen(true);
  };

  const openEdit = (t) => {
    setEditing(t);
    setForm({
      name: t.name,
      kind: t.kind,
      icon: t.icon,
      default_counts_toward_shortfall: t.default_counts_toward_shortfall ?? null,
    });
    setDialogOpen(true);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.name.trim()) {
      toast.error('Name is required');
      return;
    }
    try {
      if (editing) {
        await axios.put(`${API}/account-types/${editing.type_id}`, form, { withCredentials: true });
        toast.success('Type updated');
      } else {
        await axios.post(`${API}/account-types`, form, { withCredentials: true });
        toast.success('Type created');
      }
      setDialogOpen(false);
      setEditing(null);
      setForm(emptyForm());
      fetchTypes();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to save type');
    }
  };

  const handleDelete = (t) => {
    setPendingDelete(t);
  };

  const confirmDelete = () => {
    const t = pendingDelete;
    if (!t) return;
    setPendingDelete(null);
    setTypes((prev) => prev.filter((x) => x.type_id !== t.type_id));
    scheduleDelete({
      id: t.type_id,
      label: 'Account type',
      executor: () => axios.delete(`${API}/account-types/${t.type_id}`, { withCredentials: true }),
      onUndo: () => setTypes((prev) => [...prev, t].sort((a, b) => (a.sort_order ?? 0) - (b.sort_order ?? 0))),
      onAfterDelete: fetchTypes,
    });
  };

  const handleBulkApply = async (t, mode) => {
    // mode: 'include' | 'exclude' | 'default'
    const valueMap = { include: true, exclude: false, default: null };
    const labelMap = { include: 'include', exclude: 'exclude', default: 'reset' };
    try {
      const res = await axios.post(
        `${API}/account-types/${t.type_id}/apply-shortfall`,
        { counts_toward_shortfall: valueMap[mode] },
        { withCredentials: true }
      );
      const n = res.data?.updated ?? 0;
      toast.success(`${labelMap[mode]} applied to ${n} ${n === 1 ? 'account' : 'accounts'}`);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Bulk update failed');
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-[#0052FF]"></div>
      </div>
    );
  }

  return (
    <div className="p-6 sm:p-8" data-testid="account-types-page">
      <div className="flex items-center justify-between mb-8 gap-4 flex-wrap">
        <PageTitle
          title="Account Types"
          subtitle="Manage the categories used by your accounts. Liabilities subtract from your net worth."
          testId="account-types-title"
          className="mb-0"
        />
        <div className="flex items-center gap-3 flex-wrap">
        <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
          <DialogTrigger asChild>
            <Button onClick={openNew} className="bg-[#0052FF] text-white hover:bg-[#003ECC]" data-testid="add-account-type-button">
              <Plus className="w-4 h-4 mr-2" />
              Add Type
            </Button>
          </DialogTrigger>
          <DialogContent className="bg-white max-h-[90vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle className="text-2xl font-bold text-[#020617]">{editing ? 'Edit Account Type' : 'Add Account Type'}</DialogTitle>
            </DialogHeader>
            <form onSubmit={handleSubmit} className="space-y-4" data-testid="account-type-form">
              <div>
                <Label htmlFor="type-name">Name</Label>
                <Input
                  id="type-name"
                  value={form.name}
                  onChange={(e) => setForm({ ...form, name: e.target.value })}
                  placeholder="e.g. Crypto, Stocks ISA, Car loan"
                  required
                  maxLength={40}
                  className="mt-1.5"
                  data-testid="account-type-name-input"
                />
              </div>
              <div>
                <Label>Kind</Label>
                <Select value={form.kind} onValueChange={(v) => setForm({ ...form, kind: v })}>
                  <SelectTrigger className="mt-1.5" data-testid="account-type-kind-select">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="asset">Asset (counts toward net worth)</SelectItem>
                    <SelectItem value="liability">Liability (subtracts from net worth)</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label>Icon</Label>
                <div className="grid grid-cols-8 gap-2 mt-1.5" data-testid="icon-grid">
                  {ICON_OPTIONS.map(({ name, Icon }) => {
                    const selected = form.icon === name;
                    return (
                      <button
                        key={name}
                        type="button"
                        onClick={() => setForm({ ...form, icon: name })}
                        className={`flex items-center justify-center w-10 h-10 rounded-lg border transition-colors ${
                          selected ? 'bg-[#0052FF] border-[#0052FF] text-white' : 'bg-white border-[#E2E8F0] text-[#020617] hover:bg-[#F8FAFC]'
                        }`}
                        data-testid={`icon-option-${name}`}
                        aria-label={`Select ${name} icon`}
                      >
                        <Icon className="w-4 h-4" />
                      </button>
                    );
                  })}
                </div>
              </div>

              <div className="border-t border-[#E2E8F0] pt-4">
                <Label className="text-xs uppercase tracking-[0.2em] font-semibold text-[#64748B]">Shortfall default for new accounts</Label>
                <p className="text-xs text-[#64748B] mt-1 mb-2">
                  Used as the default when creating a new account of this type. Individual accounts can still override this.
                </p>
                <div className="space-y-2" data-testid="type-shortfall-default-options">
                  {['default', 'include', 'exclude'].map((mode) => {
                    const currentMode = form.default_counts_toward_shortfall === true ? 'include'
                      : form.default_counts_toward_shortfall === false ? 'exclude'
                      : 'default';
                    const isLiability = form.kind === 'liability';
                    const labels = {
                      default: isLiability ? 'Use kind default (exclude from shortfall)' : 'Use kind default (include in shortfall)',
                      include: isLiability
                        ? 'Include — new accounts of this type subtract their debt from spendable cash'
                        : 'Include — new accounts of this type add to spendable cash',
                      exclude: 'Exclude — new accounts of this type never affect the shortfall warning',
                    };
                    const valueMap = { default: null, include: true, exclude: false };
                    return (
                      <label key={mode} className={`flex items-start gap-3 cursor-pointer rounded-md p-2 transition-colors ${currentMode === mode ? 'bg-[#EFF6FF]' : 'hover:bg-[#F8FAFC]'}`}>
                        <input
                          type="radio"
                          name="type-shortfall-default"
                          value={mode}
                          checked={currentMode === mode}
                          onChange={() => setForm({ ...form, default_counts_toward_shortfall: valueMap[mode] })}
                          className="mt-0.5"
                          data-testid={`type-shortfall-radio-${mode}`}
                        />
                        <span className="text-xs text-[#334155] leading-relaxed">{labels[mode]}</span>
                      </label>
                    );
                  })}
                </div>
              </div>
              <div className="flex gap-3 pt-4">
                <Button type="submit" className="flex-1 bg-[#0052FF] text-white hover:bg-[#003ECC]" data-testid="account-type-submit-button">
                  {editing ? 'Update' : 'Create'}
                </Button>
                <Button type="button" onClick={() => setDialogOpen(false)} className="flex-1 bg-[#F1F5F9] text-[#020617] hover:bg-[#E2E8F0]">
                  Cancel
                </Button>
              </div>
            </form>
          </DialogContent>
        </Dialog>
        </div>
      </div>

      {types.length === 0 ? (
        <div className="bg-white border border-[#E2E8F0] rounded-lg p-12 text-center">
          <p className="text-[#64748B]">No account types yet.</p>
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4" data-testid="account-types-list">
          {types.map((t) => {
            const Icon = getAccountIcon(t.icon);
            const isLiability = t.kind === 'liability';
            return (
              <motion.div
                key={t.type_id}
                initial={FADE_IN_UP_HIDDEN}
                animate={FADE_IN_UP_SHOWN}
                className="bg-white border border-[#E2E8F0] rounded-lg p-5 hover:shadow-[0_8px_30px_rgb(0,0,0,0.04)] hover:border-[#CBD5E1] transition-all duration-300"
                data-testid={`account-type-card-${t.type_id}`}
              >
                <div className="flex items-start justify-between gap-3 mb-4">
                  <div className="flex items-center gap-3 min-w-0">
                    <div className={`w-10 h-10 rounded-lg flex items-center justify-center flex-shrink-0 ${
                      isLiability ? 'bg-[#FEE2E2] text-[#991B1B]' : 'bg-[#DBEAFE] text-[#1E40AF]'
                    }`}>
                      <Icon className="w-5 h-5" />
                    </div>
                    <div className="min-w-0">
                      <div className="text-base font-semibold text-[#020617] truncate">{t.name}</div>
                      <div className="text-xs text-[#64748B] capitalize">{t.kind}{t.is_default ? ' · default' : ''}</div>
                    </div>
                  </div>
                  <div className="flex gap-2 flex-shrink-0">
                    <button onClick={() => openEdit(t)} className="text-[#64748B] hover:text-[#0052FF] transition-colors" data-testid={`edit-type-${t.type_id}`}>
                      <Edit2 className="w-4 h-4" />
                    </button>
                    <button onClick={() => handleDelete(t)} className="text-[#64748B] hover:text-[#EF4444] transition-colors" data-testid={`delete-type-${t.type_id}`}>
                      <Trash2 className="w-4 h-4" />
                    </button>
                  </div>
                </div>
                <span className={`inline-flex items-center px-2.5 py-1 rounded-full text-xs font-medium ${
                  isLiability ? 'bg-[#FEE2E2] text-[#991B1B]' : 'bg-[#DCFCE7] text-[#166534]'
                }`}>
                  {isLiability ? 'Liability' : 'Asset'}
                </span>
                <div className="mt-4 border-t border-[#E2E8F0] pt-3" data-testid={`bulk-apply-${t.type_id}`}>
                  <div className="text-[10px] uppercase tracking-[0.2em] font-semibold text-[#64748B] mb-2">
                    Apply shortfall setting to existing accounts
                  </div>
                  <div className="flex flex-wrap gap-1.5">
                    <button
                      type="button"
                      onClick={() => handleBulkApply(t, 'include')}
                      className="px-2.5 py-1 rounded-md text-[11px] font-medium bg-[#FFEDD5] text-[#9A3412] hover:bg-[#FED7AA] transition-colors"
                      data-testid={`bulk-include-${t.type_id}`}
                    >
                      Include all
                    </button>
                    <button
                      type="button"
                      onClick={() => handleBulkApply(t, 'exclude')}
                      className="px-2.5 py-1 rounded-md text-[11px] font-medium bg-[#F1F5F9] text-[#475569] hover:bg-[#E2E8F0] transition-colors"
                      data-testid={`bulk-exclude-${t.type_id}`}
                    >
                      Exclude all
                    </button>
                    <button
                      type="button"
                      onClick={() => handleBulkApply(t, 'default')}
                      className="px-2.5 py-1 rounded-md text-[11px] font-medium text-[#64748B] hover:bg-[#F8FAFC] transition-colors"
                      data-testid={`bulk-reset-${t.type_id}`}
                    >
                      Reset to default
                    </button>
                  </div>
                </div>
              </motion.div>
            );
          })}
        </div>
      )}

      <ConfirmDialog
        open={!!pendingDelete}
        onOpenChange={(open) => { if (!open) setPendingDelete(null); }}
        title="Delete this account type?"
        description={
          pendingDelete
            ? `"${pendingDelete.name}" will be removed. If any accounts use it, the delete will fail safely.`
            : 'This cannot be undone.'
        }
        confirmLabel="Delete"
        onConfirm={confirmDelete}
        testId="delete-account-type-dialog"
      />
    </div>
  );
};

export default AccountTypes;
